import { useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ApiError } from '../../../lib/api'
import { resetPassword } from '../api'
import { AuthAlert } from '../components/AuthAlert'
import { AuthFormIntro } from '../components/AuthFormIntro'
import { AuthSplitShell } from '../components/AuthSplitShell'
import { AuthPasswordField } from '../components/AuthPasswordField'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { getPasswordStrength } from '@/lib/password-strength'
import { cn } from '@/lib/utils'

type FeedbackType = 'success' | 'error' | 'warning' | null

export function PasswordResetPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState(searchParams.get('email') ?? '')
  const token = searchParams.get('token') ?? ''
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const strength = getPasswordStrength(password)
  const [feedback, setFeedback] = useState<string | null>(null)
  const [feedbackType, setFeedbackType] = useState<FeedbackType>(null)

  const resetMutation = useMutation({
    mutationFn: resetPassword,
    onSuccess: () => {
      setFeedbackType('success')
      setFeedback(t('auth.reset.successMessage'))
      setPassword('')
      setPasswordConfirmation('')
    },
    onError: (error) => {
      setFeedbackType('error')
      setFeedback((error as ApiError).message)
    },
  })

  useEffect(() => {
    if (feedbackType === 'success') {
      const timer = setTimeout(() => {
        navigate('/login')
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [feedbackType, navigate])

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    setFeedback(null)
    setFeedbackType(null)

    const trimmedEmail = email.trim()

    if (!trimmedEmail || !password || !passwordConfirmation) {
      setFeedbackType('warning')
      setFeedback(t('auth.errors.allFieldsRequired'))
      return
    }

    if (password.length < 8) {
      setFeedbackType('warning')
      setFeedback(t('auth.errors.passwordTooShort'))
      return
    }

    if (password !== passwordConfirmation) {
      setFeedbackType('warning')
      setFeedback(t('auth.errors.passwordMismatch'))
      return
    }

    resetMutation.mutate({
      email: trimmedEmail,
      token,
      password,
      password_confirmation: passwordConfirmation,
    })
  }

  return (
    <AuthSplitShell>
      <div className="w-full space-y-5">
        <AuthFormIntro
          title={t('auth.reset.title')}
          description={t('auth.reset.description')}
        />

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="password-reset-email">
              {t('auth.email')}
            </label>
            <Input
              id="password-reset-email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              type="email"
              disabled
              className="px-4 opacity-60"
            />
          </div>

          <div className="space-y-2">
            <AuthPasswordField
              id="password-reset-new"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder={t('auth.reset.newPasswordPlaceholder')}
              label={t('auth.reset.newPasswordLabel')}
              required
              minLength={8}
            />
            {password.length > 0 && (
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                    <div
                      className={cn('h-full transition-all duration-300', strength.color)}
                      style={{ width: `${(strength.score / 5) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {t(`settings.dialogs.password.strength.${strength.label}` as const)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">{t('settings.dialogs.password.strengthLabel')}</p>
              </div>
            )}
          </div>

          <AuthPasswordField
            id="password-reset-confirm"
            autoComplete="new-password"
            value={passwordConfirmation}
            onChange={(event) => setPasswordConfirmation(event.target.value)}
            placeholder={t('auth.reset.confirmPasswordPlaceholder')}
            label={t('auth.reset.confirmPasswordLabel')}
            required
          />
          {passwordConfirmation.length > 0 && password !== passwordConfirmation && (
            <p className="text-xs text-destructive">{t('auth.errors.passwordMismatch')}</p>
          )}

          {feedback && feedbackType ? (
            <AuthAlert variant={feedbackType}>
              {feedback}
            </AuthAlert>
          ) : null}

          <Button
            size="lg"
            type="submit"
            disabled={resetMutation.isPending}
            className="w-full"
          >
            {resetMutation.isPending ? t('auth.reset.submitting') : t('auth.reset.submit')}
          </Button>
        </form>

        <div className="flex justify-center pt-2">
          <Link
            to="/login"
            className="text-sm font-medium text-foreground underline underline-offset-4 transition hover:text-foreground/80"
          >
            {t('auth.reset.backToLogin')}
          </Link>
        </div>
      </div>
    </AuthSplitShell>
  )
}
